import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useRef, useState } from 'react';

import { IPublicationCustomCssDialogsMessages, IPublicationCustomCssMessages, ISessionUser } from '../../../../types';
import { CodeEditor } from '../../../common/components/CodeEditor';
import { navigateToPublicationCustomCssView, navigateToPublicationView } from '../../../state/navigation';

import { ApplyCustomCssDialog } from './ApplyCustomCssDialog';
import { CustomCodeBlock } from './CustomCodeBlock';
import { DiscardCustomCssDialog, IDiscardCustomCssDialogProps } from './DiscardCustomCssDialog';
import { ElementInspector } from './ElementInspector';
import { Header } from './Header';
import { StoryPreview } from './StoryPreview';
import { usePublicationWithSectionsAndStories } from './hooks/usePublicationWithSectionsAndStories';
import { useStoryWithContent } from './hooks/useStoryWithContent';
import { SaveCustomCssCodeParams, useSaveCustomCssCode } from './hooks/useSaveCustomCssCode';

import { styles } from './PublicationCustomCssView.styles';
import { PageErrorBoundary } from '../../ErrorBoundary/PageErrorBoundary';
import { ViewName } from '../../../../instrumentation/views';
import { ProgressTrackerSettingsProvider } from '../../StoriesSettings/ProgressTrackerSettings/context/ProgressTracker.Provider';

export type IPublicationCustomCssProps = {
  entityId: number;
  entitySubdomain: string;
  entityUuid: string;
  publicationId: string;
  storyId: string;
  messages: IPublicationCustomCssMessages;
  dialogMessages: IPublicationCustomCssDialogsMessages;
  user: ISessionUser;
  enableGoogleTranslateForPublications?: boolean;
};

const PublicationCustomCssInternal = (props: IPublicationCustomCssProps) => {
  const {
    entityId,
    entitySubdomain,
    entityUuid,
    publicationId,
    storyId: selectedStoryId,
    messages,
    dialogMessages,
    user,
    enableGoogleTranslateForPublications
  } = props;

  // entityId can be 0 briefly after a refresh while the entity store
  // catches up to the URL subdomain — keep the publication query disabled
  // until it hydrates so we don't fire a request with the wrong scope.
  const isStoreHydrated = entityId > 0;
  const publicationQuery = usePublicationWithSectionsAndStories(
    isStoreHydrated && publicationId ? { publicationId, entityId } : null,
    messages.somethingWentWrongError
  );
  const publication = publicationQuery.data?.publication ?? null;
  const storiesSelectData = publicationQuery.data?.storiesSelectData ?? [];

  const storyQuery = useStoryWithContent(
    selectedStoryId ? { storyId: selectedStoryId } : null,
    messages.somethingWentWrongError
  );
  const selectedStory = storyQuery.data ?? null;

  // `isFetching` rather than `isLoading`: in TanStack Query v4, a disabled
  // query reports `isLoading: true` forever because `status` stays
  // `'loading'` until data arrives — that would lock the story dropdown
  // whenever no story is selected.
  const isLoading = publicationQuery.isFetching || storyQuery.isFetching;

  const { saveCustomCssCode, isLoading: isApplying } = useSaveCustomCssCode(
    { publicationId },
    {
      errorTitle: messages.somethingWentWrongError,
      errorBody: messages.changesSavedFailed,
      successTitle: messages.changesApplied,
      successBody: messages.changesAppliedSuccessfully
    }
  );

  const [activeElement, setActiveElement] = useState<HTMLElement | null>(null);
  const [publicationCss, setPublicationCss] = useState('');
  const [publicationSavedCss, setPublicationSavedCss] = useState('');
  const [storyCss, setStoryCss] = useState('');
  const [storySavedCss, setStorySavedCss] = useState('');
  const [isEditingGlobalCss, setIsEditingGlobalCss] = useState(false);
  const iframeDocumentRef = useRef<Document | null>(null);
  const storyCodeEditorRef = useRef<CodeEditor | null>(null);
  const publicationCodeEditorRef = useRef<CodeEditor | null>(null);
  const [discardModalProps, setDiscardModalProps] =
    useState<Pick<IDiscardCustomCssDialogProps, 'onDiscard'> | null>(null);
  const [showApplyStylingModal, setShowApplyStylingModal] = useState<boolean>(false);

  const isPublicationCssChanged = publicationCss !== publicationSavedCss;
  const isStoryCssChanged = storyCss !== storySavedCss;
  const hasUnsavedChanges = isPublicationCssChanged || isStoryCssChanged;

  const actionsDisabled = isLoading || !hasUnsavedChanges;

  const discardChanges = () => {
    setPublicationCss(publicationSavedCss || '');
    setStoryCss(storySavedCss || '');
  };

  const handleExit = () => {
    if (hasUnsavedChanges) {
      setDiscardModalProps({
        onDiscard: () => {
          discardChanges();
          navigateToPublicationView(entitySubdomain, publicationId);
        }
      });
    } else {
      navigateToPublicationView(entitySubdomain, publicationId);
    }
  };

  const handleCloseDiscardDialog = () => {
    setDiscardModalProps(null);
  };

  const handleStoryChange = (storyId: string) => {
    if (hasUnsavedChanges) {
      setDiscardModalProps({
        onDiscard: () => {
          discardChanges();
          handleCloseDiscardDialog();
          navigateToPublicationCustomCssView(entitySubdomain, publicationId, storyId);
        }
      });
    } else {
      navigateToPublicationCustomCssView(entitySubdomain, publicationId, storyId);
    }
  };

  const onCopySelector = (selector: string) => {
    let updatedCode = isEditingGlobalCss ? publicationCss : storyCss;
    if (updatedCode) updatedCode += `\n`;
    updatedCode += `${selector} {\n  \n}`;
    (isEditingGlobalCss ? setPublicationCss : setStoryCss)(updatedCode);
  };

  const onDiscardBtnClick = () => {
    setDiscardModalProps({
      onDiscard: () => {
        discardChanges();
        handleCloseDiscardDialog();
      }
    });
  };

  const handleCloseApplyStylingModal = () => {
    setShowApplyStylingModal(false);
  };

  const applyChanges = () => {
    handleCloseApplyStylingModal();
    const params: SaveCustomCssCodeParams = {
      publicationId,
      userId: user.id,
      entityId,
      entitySubdomain,
      entityUuid,
      enableGoogleTranslateForPublications
    };
    if (isPublicationCssChanged && publication) {
      params.publication = { ...publication, customCssCode: publicationCss };
    }
    if (isStoryCssChanged && selectedStory) {
      params.story = { ...selectedStory, customCssCode: storyCss };
    }

    if (!params.publication && !params.story) return;
    void saveCustomCssCode(params).then(() => {
      setStorySavedCss(storyCss);
      storyCodeEditorRef.current?.clearHistory();
      setPublicationSavedCss(publicationCss);
      publicationCodeEditorRef.current?.clearHistory();
    });
  };

  const onSaveBtnClick = () => {
    if (!isPublicationCssChanged) {
      setShowApplyStylingModal(false);
      applyChanges();
    } else {
      setShowApplyStylingModal(true);
    }
  };

  // Reset editor draft + active element when the user switches stories.
  useEffect(() => {
    setStorySavedCss('');
    setStoryCss('');
    setActiveElement(null);
  }, [selectedStoryId]);

  useEffect(() => {
    if (publication) {
      const css = publication.customCssCode || '';
      setPublicationSavedCss(css);
      setPublicationCss(css);
      publicationCodeEditorRef.current?.clearHistory();
    }
  }, [publication]);

  useEffect(() => {
    if (selectedStory) {
      const css = selectedStory.customCssCode || '';
      setStorySavedCss(css);
      setStoryCss(css);
      storyCodeEditorRef.current?.clearHistory();
    }
  }, [selectedStory]);

  return (
    <Box sx={styles.container}>
      {!!discardModalProps && (
        <DiscardCustomCssDialog
          messages={dialogMessages}
          onDiscard={discardModalProps.onDiscard}
          onCancel={handleCloseDiscardDialog}
        />
      )}
      {!!showApplyStylingModal && (
        <ApplyCustomCssDialog
          messages={dialogMessages}
          onApply={applyChanges}
          onCancel={handleCloseApplyStylingModal}
        />
      )}
      <Header
        isLoading={isLoading}
        isApplying={isApplying}
        messages={messages}
        publicationTitle={publication?.title ?? ''}
        storiesSelectData={storiesSelectData}
        selectedStoryId={selectedStoryId}
        onStoryChange={handleStoryChange}
        onExit={handleExit}
      />
      <Box sx={styles.content}>
        <ProgressTrackerSettingsProvider entityId={selectedStory?.contents?.[0]?.entityId || entityId}>
          <StoryPreview
            activeElement={activeElement}
            entityId={entityId}
            iframeDocumentRef={iframeDocumentRef}
            messages={messages}
            selectedStoryId={selectedStoryId}
            story={storyQuery.isFetching ? null : selectedStory}
            storyCss={storyCss}
            publicationCss={publicationCss}
            setActiveElement={setActiveElement}
          />
        </ProgressTrackerSettingsProvider>
        <Box sx={styles.devTools}>
          <ElementInspector
            activeElement={activeElement}
            iframeDocumentRef={iframeDocumentRef}
            messages={messages}
            onCopySelector={onCopySelector}
            setActiveElement={setActiveElement}
          />
          <CustomCodeBlock
            story={selectedStory}
            messages={messages}
            publicationCss={publicationCss}
            publicationCodeEditorRef={publicationCodeEditorRef}
            onPublicationCssChange={setPublicationCss}
            storyCss={storyCss}
            storyCodeEditorRef={storyCodeEditorRef}
            onStoryCssChange={setStoryCss}
            isEditingGlobalCss={isEditingGlobalCss}
            setIsEditingGlobalCss={setIsEditingGlobalCss}
          />
          <Box sx={styles.footer}>
            <Button
              variant="contained"
              color="primary"
              disabled={actionsDisabled || isApplying}
              startIcon={isApplying ? <CircularProgress size={16} color="inherit" aria-label="Loading" /> : undefined}
              data-test="saveAndApplyBtn"
              onClick={onSaveBtnClick}
            >
              {isApplying ? messages.applying : messages.saveAndApply}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              disabled={actionsDisabled || isApplying}
              data-test="discardBtn"
              onClick={onDiscardBtnClick}
            >
              {messages.discard}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const PublicationCustomCss = (props: IPublicationCustomCssProps) => {
  return (
    <PageErrorBoundary viewName={ViewName.PublicationCustomCssViewPage}>
      <PublicationCustomCssInternal {...props} />
    </PageErrorBoundary>
  );
};
